import { forwardRef, useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface AutocompleteInputProps {
  value: string;
  onChange: (val: string) => void;
  options: (string | { label: string; value: string })[];
  placeholder?: string;
  onEnterNext?: () => void;
  className?: string;
  dropdownMinWidth?: number; // New prop for minimum dropdown width
  showAllOnFocus?: boolean; // NEW: Show all options on focus/click
}

const AutocompleteInput = forwardRef<HTMLInputElement, AutocompleteInputProps>(
  ({
    value,
    onChange,
    options,
    placeholder = "Search...",
    onEnterNext,
    className,
    dropdownMinWidth = 200, // Default minimum width of 200px
    showAllOnFocus = false // NEW: Default to false for backward compatibility
  }, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const optionRefs = useRef<(HTMLDivElement | null)[]>([]);

    const filtered = options
      .map((opt) => typeof opt === "string" ? opt : opt.label)
      .filter((opt) => {
        // NEW: If showAllOnFocus is true and no search value, show all options
        if (showAllOnFocus && !value.trim()) {
          return true;
        }
        // Otherwise filter by search value
        return opt.toLowerCase().includes(value.toLowerCase());
      })
      .slice(0, 8); // Limit to 8 options for performance

    useEffect(() => {
      setHighlightedIndex(0);
    }, [value]);

    // Scroll highlighted item into view when highlightedIndex changes
    useEffect(() => {
      if (isOpen && optionRefs.current[highlightedIndex] && dropdownRef.current) {
        const highlightedElement = optionRefs.current[highlightedIndex];
        const dropdown = dropdownRef.current;
        
        if (highlightedElement) {
          // Calculate positions relative to the dropdown container
          const dropdownScrollTop = dropdown.scrollTop;
          const dropdownHeight = dropdown.clientHeight;
          const elementOffsetTop = highlightedElement.offsetTop;
          const elementHeight = highlightedElement.offsetHeight;
          
          // Check if element is above visible area
          if (elementOffsetTop < dropdownScrollTop) {
            dropdown.scrollTop = elementOffsetTop;
          }
          // Check if element is below visible area
          else if (elementOffsetTop + elementHeight > dropdownScrollTop + dropdownHeight) {
            dropdown.scrollTop = elementOffsetTop + elementHeight - dropdownHeight;
          }
        }
      }
    }, [highlightedIndex, isOpen]);

    // Reset option refs array when filtered options change
    useEffect(() => {
      optionRefs.current = optionRefs.current.slice(0, filtered.length);
      // Ensure highlightedIndex is within bounds when options change
      if (highlightedIndex >= filtered.length && filtered.length > 0) {
        setHighlightedIndex(filtered.length - 1);
      }
    }, [filtered.length, highlightedIndex]);

    // Calculate dropdown positioning and width
    const getDropdownStyle = () => {
      if (!containerRef.current) return {};
      
      const containerRect = containerRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      // Calculate minimum width (either container width or dropdownMinWidth, whichever is larger)
      const minWidth = Math.max(containerRect.width, dropdownMinWidth);
      
      // Check if dropdown would overflow viewport on the right
      const wouldOverflowRight = containerRect.left + minWidth > viewportWidth - 20;
      
      // Check if dropdown would overflow viewport at bottom
      const dropdownHeight = Math.min(filtered.length * 40 + 16, 200); // Estimate dropdown height
      const wouldOverflowBottom = containerRect.bottom + dropdownHeight > viewportHeight - 20;
      
      return {
        minWidth: `${minWidth}px`,
        width: 'auto',
        ...(wouldOverflowRight && {
          right: 0,
          left: 'auto'
        }),
        ...(wouldOverflowBottom && {
          bottom: '100%',
          top: 'auto',
          marginBottom: '4px',
          marginTop: '0'
        })
      };
    };

    return (
      <div ref={containerRef} className="relative">
        <Input
          ref={ref}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            // Show dropdown when user is typing OR when showAllOnFocus is true
            if (e.target.value.trim() || showAllOnFocus) {
              setIsOpen(true);
            } else {
              setIsOpen(false);
            }
          }}
          onFocus={() => {
            // NEW: Show dropdown on focus if showAllOnFocus is true, or if there's text with matches
            if (showAllOnFocus || (value && value.trim() && filtered.length > 0)) {
              setIsOpen(true);
            }
          }}
          onClick={() => {
            // NEW: Show dropdown on click if showAllOnFocus is true, or if there's text with matches
            if (showAllOnFocus || (value && value.trim() && filtered.length > 0)) {
              setIsOpen(true);
            }
          }}
          onBlur={() => setTimeout(() => setIsOpen(false), 200)}
          onKeyDown={(e) => {
            if (!isOpen && filtered.length > 0 && (value || showAllOnFocus)) {
              setIsOpen(true);
              setHighlightedIndex(0); // Reset to first item when opening
              return;
            }
            
            if (!isOpen) {
              if (e.key === "Enter" && onEnterNext) {
                e.preventDefault();
                onEnterNext();
              }
              return;
            }

            if (e.key === "ArrowDown") {
              e.preventDefault();
              const newIndex = Math.min(highlightedIndex + 1, filtered.length - 1);
              setHighlightedIndex(newIndex);
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              const newIndex = Math.max(highlightedIndex - 1, 0);
              setHighlightedIndex(newIndex);
            } else if (e.key === "Enter") {
              e.preventDefault();
              if (filtered.length > 0 && highlightedIndex >= 0) {
                onChange(filtered[highlightedIndex]);
                setIsOpen(false);
                if (onEnterNext) setTimeout(() => onEnterNext(), 100);
              } else if (onEnterNext) {
                onEnterNext();
              }
            } else if (e.key === "Escape") {
              setIsOpen(false);
            } else if (e.key === "Tab") {
              setIsOpen(false);
              // Let tab work normally
            }
          }}
          placeholder={placeholder}
          className={cn("h-8", className)}
          autoComplete="off"
        />

        {/* Enhanced dropdown with minimum width and smart positioning */}
        {isOpen && (showAllOnFocus || filtered.length > 0) && (
          <div
            ref={dropdownRef}
            className="absolute z-dropdown bg-popover shadow-lg border border-border rounded-md max-h-48 overflow-auto text-sm ring-1 ring-border focus:outline-none"
            style={{
              top: '100%',
              left: 0,
              marginTop: '4px',
              ...getDropdownStyle()
            }}
          >
            {filtered.map((option, i) => (
              <div
                key={`${option}-${i}`}
                ref={(el) => {
                  optionRefs.current[i] = el;
                }}
                className={cn(
                  "px-3 py-2 cursor-pointer transition-colors first:rounded-t-md last:rounded-b-md whitespace-nowrap",
                  i === highlightedIndex
                    ? "bg-primary text-primary-foreground"
                    : "hover:bg-accent text-foreground"
                )}
                onMouseEnter={() => setHighlightedIndex(i)}
                onMouseDown={(e) => {
                  e.preventDefault(); // Prevent input blur
                  onChange(option);
                  setIsOpen(false);
                  if (onEnterNext) setTimeout(() => onEnterNext(), 100);
                }}
              >
                <div className="font-medium truncate">{option}</div>
              </div>
            ))}
          </div>
        )}

        {/* Show "No results" when typing but no matches */}
        {isOpen && filtered.length === 0 && value.trim() && !showAllOnFocus && (
          <div
            className="absolute z-dropdown bg-popover shadow-lg border border-border rounded-md text-sm ring-1 ring-border"
            style={{
              top: '100%',
              left: 0,
              marginTop: '4px',
              ...getDropdownStyle()
            }}
          >
            <div className="px-3 py-2 text-muted-foreground italic text-center">
              No matches found. Press <kbd className="px-1 py-0.5 bg-muted border border-border rounded text-xs">Enter</kbd> to continue.
            </div>
          </div>
        )}
      </div>
    );
  }
);

AutocompleteInput.displayName = "AutocompleteInput";
export default AutocompleteInput;